import { Directive, EventEmitter, HostListener, Inject, Output
       } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';

import { Svg, Element as SvgElement } from '@svgdotjs/svg.js';
import { UnchangedPlaceholdersChoice, UnchangedPlaceholdersContext,
         UnchangedPlaceholdersDialogComponent
       } from 'app/dialogs/unchanged-placeholders-dialog/unchanged-placeholders-dialog.component'; // eslint-disable-line
import { PlaceholderService
       } from 'collage-designer/stage/placeholder/placeholder.service';
import { StageService } from 'collage-designer/stage/stage.service';
import { GlobalConstants, ProgressConstants, StageConstants
       } from 'core/constants';
import { Globals } from 'core/globals';
import { Severity } from 'core/logging/constants';
import { LoggingService } from 'core/logging/logging.service';

import { ProductService } from 'core/product/product.service';
import { EventTrackingService, TrackingObject
       } from 'core/tracking/event-tracking.service';
import { StageUtils } from 'core/utils.stage';
import { ProgressService } from './progress.service';


export enum ValidationFailureReason
{
  PLACEHOLDERS_UNCHANGED,
}

interface ChangeableItem
{
  wrapper: Svg;
  placeholder: SvgElement;
  kind: StageUtils.PlaceholderKind;
}

@Directive({
    selector: '[validate-design-step]',
    standalone: false
})
export class ValidateDesignStepDirective
{
  private errorsOccurred: Array<String> = [];

  @Output() public validationDone: EventEmitter<boolean> = new EventEmitter();
  @Output() public validationFailed: EventEmitter<ValidationFailureReason> =
      new EventEmitter();

  constructor(
    private dialog: MatDialog,
    @Inject(ProgressService) private progressService: ProgressService,
    @Inject(StageService) private stageService: StageService,
    @Inject(PlaceholderService) private placeholderService: PlaceholderService,
    @Inject(ProductService) private productService: ProductService,
    @Inject(EventTrackingService) private eventTrackingService:
        EventTrackingService,
    @Inject(LoggingService) private loggingService: LoggingService
  )
  {}

  @HostListener('click', ['$event'])
  public async checkPlaceholder(event):
      Promise<boolean>
  {
    const trackEvent = () => {
      const progressId = this.progressService.getNextProgress() ?
          this.progressService.getNextProgress().id : 'finalize';
      let trackingObject = <TrackingObject> {
        category: this.productService.getCurrentProduct().id,
        action: 'progress_change_start_' + progressId,
      };
      if (this.errorsOccurred.length > 0) {
        trackingObject.label = this.errorsOccurred.join('|');
      }
      this.eventTrackingService.track(trackingObject);
      this.errorsOccurred = [];
    };

    if (this.progressService.getCurrentProgress().group ===
        ProgressConstants.GROUP_COLLAGEDESIGNER)
    {
      if (! await this.getUnchangedPlaceholdersPromise()) {
        this.validationFailed.emit(
            ValidationFailureReason.PLACEHOLDERS_UNCHANGED);

        trackEvent();
        return false;
      }
    }

    trackEvent();
    this.validationDone.emit(true);
    return true;
  }

  private getChangeableItems(): Array<ChangeableItem>
  {
    const stage = this.stageService.getCurrentStageReference();

    if (! stage) {
      return [];
    }

    const toItem = (wrapper: Svg) => {
      const image = wrapper.find('image').at(0);
      const text = wrapper.find('text').at(0);

      return {
        wrapper: wrapper,
        placeholder: image || text,
        kind: image ? StageUtils.PlaceholderKind.IMAGE :
                      StageUtils.PlaceholderKind.TEXT,
      };
    };

    return (stage as any)
      .find('*')
      .filter(el => StageUtils.getBooleanAttribute(
          el.node, StageConstants.ATTRIBUTE_CONTENT_CHANGEABLE))
      .map(toItem);
  }

  private buildPlaceholderContext(
      items: Array<ChangeableItem>): Array<StageUtils.PlaceholderContext>
  {
    const isUnchanged = it => StageUtils.getBooleanAttribute(
      it.placeholder.node, StageConstants.ATTRIBUTE_IS_UNCHANGED);

    const hintIfUnchanged = it => StageUtils.getBooleanAttribute(
      it.placeholder.node, StageConstants.ATTRIBUTE_HINT_IF_UNCHANGED, true);

    const analyzeItem = (it: ChangeableItem) => ({
      id: it.placeholder.attr('id'),
      kind: it.kind,
      node: it.placeholder.node,
    });

    return items
      .filter(isUnchanged)
      .filter(hintIfUnchanged)
      .map(analyzeItem);
  }

  private async getUnchangedPlaceholdersPromise(): Promise<boolean>
  {
    if (Globals.mode === GlobalConstants.MODE_GOD) {
      return true;
    }

    const changeableItems = this.getChangeableItems();

    const context: UnchangedPlaceholdersContext = {
      componentType: StageUtils.getComponentType(
          this.stageService.getCurrentStageReference()),
      placeholders: this.buildPlaceholderContext(changeableItems),
      changeableElementsCount: changeableItems.length,
    };

    if (context.placeholders.length < 1) {
      return true;
    }

    this.errorsOccurred.push('unchangedPlaceholders');

    const choice = await this.dialog
      .open(
          UnchangedPlaceholdersDialogComponent,
          {data: context, panelClass: 'dialog-pane'})
      .afterClosed()
      .toPromise();

    return await this.evaluateUnchangedPlaceholdersChoice(context, choice);
  }

  private async evaluateUnchangedPlaceholdersChoice(
    ctx: UnchangedPlaceholdersContext,
    choice: UnchangedPlaceholdersChoice): Promise<boolean>
  {
    const logDialog = message => {
      this.loggingService.log(
          'ValidateDesignStepDirective',
          Severity.WARNING,
          this.progressService.getCurrentProgress().id + ': ' + message);
    };
    const clearPlaceholders = (
        placeholders: Array<StageUtils.PlaceholderContext>) => {
          for (const placeholder of placeholders) {
            this.stageService.clearElement(placeholder.id);
          }
    };

    switch (choice) {
      case UnchangedPlaceholdersChoice.FIX: return false;
      case UnchangedPlaceholdersChoice.CLICK_IMAGE: {
        await this.placeholderService.clickAnyUnchangedImagePlaceholder();
        return false;
      }
      case UnchangedPlaceholdersChoice.CLICK_TEXT: {
        await this.placeholderService.clickAnyUnchangedTextPlaceholder();
        return false;
      }
      case UnchangedPlaceholdersChoice.CLICK_TITLE: {
        await this.placeholderService.clickTitleTextPlaceholder();
        return false;
      }
      case UnchangedPlaceholdersChoice.IGNORE: // FALL THROUGH
      case UnchangedPlaceholdersChoice.IGNORE_IMAGES_CLEAR_TITLE: {
        clearPlaceholders(ctx.placeholders.filter(
            p => StageUtils.isVolatileText(ctx.componentType, p)));
        logDialog('ignore empty placeholders');
        return true;
      }
      case UnchangedPlaceholdersChoice.CLEAR: {
        logDialog('clear placeholders');
        clearPlaceholders(ctx.placeholders);
        return true;
      }
      default: return false;
    }
  }
}
